import { Box, Grid, makeStyles, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { LinkedPatient } from 'hydra-data-types';
import { FunctionComponent, useEffect } from 'react';
import { CopyReferralLinkButton } from '../../../components/CopyReferralLinkButton';
import { PageContainer } from '../../../components/PageContainer';
import { ReferralOnePagerButton } from '../../../components/ReferralOnePagerButton';
import { TopBar } from '../../../components/TopBar';
import { CONNECT_SCREENS } from '../../../constants';
import { Analytics } from '../../../helpers/analytics/Analytics';
import { AnalyticsEventName } from '../../../helpers/analytics/AnalyticsEvent';
import { useLinkedPatients } from '../../../hooks/useLinkedPatients';
import { PatientCard } from './PatientCard';
import { ReferralSteps } from './ReferralSteps';

export const LinkedPatientsScreen: FunctionComponent = () => {
  const linkedPatients = useLinkedPatients();

  useEffect(function trackAnalytics() {
    Analytics.track({
      event: AnalyticsEventName.h_screen_viewed,
      screen: CONNECT_SCREENS.LINKED_PATIENTS,
    });
  }, []);

  return (
    <PageContainer testId="linked-patients-screen">
      <TopBar
        title="Linked Nerva Patients"
        showReferralButton={linkedPatients?.length !== 0}
        description="Link Nerva patients to see their progress.
			This feature is currently only available for Nerva.
			To refer a patient to Evia, Finito, Claria or Relio,
			simply ask them to download the program from the App Store or Play Store."
      />

      <Box display="flex" flexDirection="column">
        {linkedPatients?.length === 0 ? (
          <NoLinkedPatients />
        ) : (
          <Patients linkedPatients={linkedPatients} />
        )}
        <ReferralSteps expanded={linkedPatients?.length === 0} />
      </Box>
    </PageContainer>
  );
};

const NoLinkedPatients: FunctionComponent = () => {
  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'));
  const classes = useStyles();

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Box paddingX={isMobile ? 0 : 25}>
        <Typography variant="body2">
          You don&apos;t seem to be linked to any of your patients yet. Click the button below to
          refer Nerva with your referral link.
        </Typography>
        <em>
          <Typography variant="body2">
            Patients are 3X more likely to start the program, and 2X more likely to see results when
            referred by a practitioner referral link.
          </Typography>
        </em>
      </Box>
      <Box paddingTop={4} paddingBottom={5} className={classes.referralButtons}>
        <CopyReferralLinkButton />
        <ReferralOnePagerButton />
      </Box>
    </Box>
  );
};

type PatientsProps = { linkedPatients: LinkedPatient[] | undefined };
const Patients: FunctionComponent<PatientsProps> = (props) => {
  const classes = useStyles();
  return (
    <Grid container spacing={2} data-testid="patient-progress">
      {props.linkedPatients?.map((linkedPatient) => (
        <Grid item xs={12} sm={6} md={4} key={linkedPatient.email} className={classes.patientCard}>
          <PatientCard linkedPatient={linkedPatient} />
        </Grid>
      ))}
    </Grid>
  );
};

const useStyles = makeStyles({
  patientCard: {
    minWidth: 333,
  },
  referralButtons: {
    display: 'flex',
    gap: 32,
  },
});
